import { useEffect, useState } from 'react';
import { api } from './api/client';
import { useWorkspaceStore } from './store/workspaceStore';
import { useLayoutStore } from './store/layoutStore';
import { EventBrowser } from './components/EventBrowser';
import { ChannelGraphs } from './components/graphs/ChannelGraphs';
import { PlaybackBar } from './components/graphs/PlaybackBar';
import { TrackMap } from './components/map/TrackMap';
import { ChannelConfig } from './components/ChannelConfig';
import { ImportDialog } from './components/ImportDialog';
import { ToastHost } from './components/ToastHost';

const clamp = (v: number, lo: number, hi: number): number => Math.min(Math.max(v, lo), hi);

/** Track whether the viewport is narrow enough to use the mobile layout. */
function useIsMobile(breakpoint = 820): boolean {
  const query = `(max-width: ${breakpoint}px)`;
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (): void => setMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return mobile;
}

type MobileTab = 'laps' | 'map' | 'graphs';

/** Read a share id from ?share=… or #/view/… on load. */
function shareIdFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const q = params.get('share');
  if (q) return q;
  const m = window.location.hash.match(/view\/([\w-]+)/);
  return m ? m[1] : null;
}

export function App(): React.ReactElement {
  const init = useWorkspaceStore((s) => s.init);
  const error = useWorkspaceStore((s) => s.error);
  const loadViewState = useWorkspaceStore((s) => s.loadViewState);
  const currentViewState = useWorkspaceStore((s) => s.currentViewState);
  const referenceLap = useWorkspaceStore((s) => s.referenceLap);

  const leftWidth = useLayoutStore((s) => s.leftWidth);
  const rightWidth = useLayoutStore((s) => s.rightWidth);
  const setLeftWidth = useLayoutStore((s) => s.setLeftWidth);
  const setRightWidth = useLayoutStore((s) => s.setRightWidth);
  const leftCollapsed = useLayoutStore((s) => s.leftCollapsed);
  const toggleLeftCollapsed = useLayoutStore((s) => s.toggleLeftCollapsed);

  const [importing, setImporting] = useState(false);
  const [shareMsg, setShareMsg] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState<MobileTab>('laps');

  function startDrag(e: React.MouseEvent, which: 'left' | 'right'): void {
    e.preventDefault();
    const startX = e.clientX;
    const startLeft = leftWidth;
    const startRight = rightWidth;
    const onMove = (ev: MouseEvent): void => {
      const dx = ev.clientX - startX;
      if (which === 'left') setLeftWidth(clamp(startLeft + dx, 220, 640));
      else setRightWidth(clamp(startRight - dx, 280, 760));
    };
    const onUp = (): void => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  useEffect(() => {
    void (async () => {
      await init();
      const shareId = shareIdFromUrl();
      if (shareId) {
        try {
          const share = await api.getShare(shareId);
          await loadViewState(share.viewState);
        } catch (err) {
          console.error('Failed to load shared view', err);
          setShareMsg('This share link could not be loaded.');
          setTimeout(() => setShareMsg(null), 4000);
        }
      }
    })();
  }, [init, loadViewState]);

  async function share(): Promise<void> {
    const view = currentViewState();
    if (!view) {
      setShareMsg('Select a reference lap first.');
      return;
    }
    try {
      const { id } = await api.createShare(view);
      const url = `${window.location.origin}${window.location.pathname}?share=${id}`;
      await navigator.clipboard.writeText(url).catch(() => undefined);
      setShareMsg('Share link copied to clipboard.');
    } catch (err) {
      setShareMsg((err as Error).message);
    }
    setTimeout(() => setShareMsg(null), 4000);
  }

  if (isMobile) {
    return (
      <div className="app mobile">
        <div className="topbar">
          <h1>Team Lap Analysis</h1>
          <div className="spacer" />
          <button className="primary" onClick={() => setImporting(true)}>
            Import
          </button>
          <button onClick={() => void share()} disabled={!referenceLap}>
            Share
          </button>
        </div>
        {shareMsg && (
          <div className="mobile-msg small muted" role="status">
            {shareMsg}
          </div>
        )}
        {error && (
          <div className="mobile-msg error" role="alert">
            {error}
          </div>
        )}

        <div className="mobile-view">
          {mobileTab === 'laps' && <EventBrowser />}
          {mobileTab === 'map' && (
            <div className="col-right">
              <TrackMap />
              <ChannelConfig />
            </div>
          )}
          {mobileTab === 'graphs' && <ChannelGraphs showPlayback={false} />}
        </div>

        {/* Persistent playback bar: rendered once at the app level (not inside a
            tab) so the animation clock keeps running while switching between the
            Graphs and Map tabs. Returns null until a reference lap is loaded. */}
        <div className="mobile-playback">
          <PlaybackBar />
        </div>

        <nav className="mobile-tabs" aria-label="Views">
          <button
            className={mobileTab === 'laps' ? 'active' : ''}
            onClick={() => setMobileTab('laps')}
          >
            Laps
          </button>
          <button
            className={mobileTab === 'graphs' ? 'active' : ''}
            onClick={() => setMobileTab('graphs')}
          >
            Graphs
          </button>
          <button
            className={mobileTab === 'map' ? 'active' : ''}
            onClick={() => setMobileTab('map')}
          >
            Map
          </button>
        </nav>

        {importing && <ImportDialog onClose={() => setImporting(false)} />}
        <ToastHost />
      </div>
    );
  }

  return (
    <div className="app">
      <div className="topbar">
        <button
          className="icon-btn"
          onClick={toggleLeftCollapsed}
          title={leftCollapsed ? 'Show sidebar' : 'Hide sidebar'}
          aria-label="Toggle sidebar"
        >
          ☰
        </button>
        <h1>Team Lap Analysis</h1>
        <button className="primary" onClick={() => setImporting(true)}>
          Import
        </button>
        <button onClick={() => void share()} disabled={!referenceLap}>
          Share view
        </button>
        {shareMsg && <span className="small muted">{shareMsg}</span>}
        <div className="spacer" />
        {error && <span className="error" style={{ padding: 0 }}>{error}</span>}
      </div>

      <div
        className="workspace"
        style={{
          gridTemplateColumns: leftCollapsed
            ? `0px 0px minmax(0, 1fr) 6px ${rightWidth}px`
            : `${leftWidth}px 6px minmax(0, 1fr) 6px ${rightWidth}px`,
        }}
      >
        {leftCollapsed ? <div /> : <EventBrowser />}
        {leftCollapsed ? (
          <div />
        ) : (
          <div className="col-resizer" onMouseDown={(e) => startDrag(e, 'left')} title="Drag to resize" />
        )}
        <ChannelGraphs />
        <div className="col-resizer" onMouseDown={(e) => startDrag(e, 'right')} title="Drag to resize" />
        <div className="col-right">
          <TrackMap />
          <ChannelConfig />
        </div>
      </div>

      {importing && <ImportDialog onClose={() => setImporting(false)} />}
      <ToastHost />
    </div>
  );
}
